/**
 * AdminButton
 * Consistent button variants for admin actions.
 * Wraps shadcn Button with pre-configured admin styles.
 */

import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

type AdminButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

interface AdminButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: AdminButtonVariant;
  isLoading?: boolean;
  loadingText?: string;
}

const variantStyles: Record<AdminButtonVariant, string> = {
  primary:
    'bg-content-primary text-white border border-content-primary hover:bg-content-primary/90',
  secondary:
    'bg-white text-content-primary border border-content-primary hover:bg-content-primary hover:text-white',
  danger:
    'bg-white text-red-600 border border-red-200 hover:bg-red-50',
  ghost:
    'bg-transparent text-content-primary border border-transparent hover:bg-gray-100',
};

export function AdminButton({
  variant = 'secondary',
  isLoading = false,
  loadingText,
  children,
  className,
  disabled,
  ...props
}: AdminButtonProps) {
  return (
    <Button
      className={cn(
        'min-h-11 font-light transition-all',
        variantStyles[variant],
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {loadingText || 'Cargando...'}
        </>
      ) : (
        children
      )}
    </Button>
  );
}
